/**
 * Insurance vertical codes, as they appear in the product UID (`HK.TI.MSIG…` →
 * `TI`).
 *
 * The codebase has no single enumeration of vertical codes - they are spelled
 * out ad hoc wherever a vertical matters (`getProductType`, `bankingVertical`,
 * `dialogTitleMap`, …). This list is the one place the reward journey decides
 * "is this an insurance product", so the copy that is worded for insurance
 * stays in sync between the reward offer section and the reward sticky footer.
 */
export const INSURANCE_VERTICAL_CODES = [
  'TI', // Travel
  'CI', // Car
  'LFI', // Life
  'HI', // Home
  'MI', // Medical
  'PA', // Personal accident
  'DHI', // Domestic helper
] as const;

/**
 * The rewards vertical that groups every insurance product
 * (`productVertical.code`, the same value the GE deeplink check reads in
 * `apply/[productId]/redirect`). It is coarser than the UID prefix - it says
 * "insurance" without saying which kind - which is exactly what makes it a
 * safe net for an insurance product whose UID prefix is not in the list above.
 */
const REWARDS_INSURANCE_VERTICAL_CODE = 'INS';

/** The vertical code carried by a product UID, e.g. `HK.TI.MSIG…` → `TI`. */
function getVerticalCode(productUid?: string | null): string {
  return productUid?.split('.')[1] ?? '';
}

/**
 * A lookup keyed on vertical code that answers `value` for every insurance
 * vertical and `undefined` for everything else.
 *
 * Adding a code to {@link INSURANCE_VERTICAL_CODES} extends every map built
 * here, so no call site has to be touched and no insurance vertical can be
 * covered in one place but forgotten in another.
 */
function insuranceVerticalMap<T>(value: T): Record<string, T | undefined> {
  return Object.fromEntries(
    INSURANCE_VERTICAL_CODES.map((code) => [code, value]),
  );
}

/**
 * Whether the reward journey should use the insurance-worded copy for this
 * product - the one door both the reward offer section and the reward sticky
 * footer ask.
 *
 * The UID prefix decides first, because it is the precise signal. When it says
 * nothing - an insurance vertical not enumerated above, or a product without a
 * UID - the coarse rewards vertical still identifies the product as insurance,
 * so a new insurance code cannot silently fall back to the banking copy.
 */
export function isInsuranceProduct(product?: {
  catalogueProduct?: { productUid?: string | null } | null;
  productVertical?: { code?: string | null } | null;
}): boolean {
  return (
    insuranceVerticalMap(true)[
      getVerticalCode(product?.catalogueProduct?.productUid)
    ] ?? product?.productVertical?.code === REWARDS_INSURANCE_VERTICAL_CODE
  );
}
